import styles from './styles/Show.module.scss';
import useScreenSize from 'hooks/useScreenSize';
import { ClaimableEntityType, TeamsShowQuery } from 'queries';
import PageLayout from '@/components/PageLayout';
import ContentSection, {
  ContentSectionFlushContent,
  ContentSectionFooterButton,
  ContentSectionHeader,
  ContentSectionHeaderLink
} from '@/components/ContentSection';
import ApplicationShareButton from 'containers/ApplicationContainer/components/ApplicationShareButton';
import StandingsTable from '@/components/StandingsTable';
import InfoBlock from '@/components/InfoBlock';
import Button, { ButtonSize, ButtonVariant, ButtonWidth } from '@/components/Button';
import { entryPoint } from 'react_rails/entryPoint';
import Page from '@/components/Page';
import TeamSubNav from '@/components/subNavs/TeamSubNav';
import CalloutModule from '@/components/modules/CalloutModule';
import LocalizedDate from 'components/LocalizedDate';
import GoFanButton from '@/components/affiliates/GoFanButton';
import { COMPLETED, UPCOMING } from 'constants/GameStatuses';
import NFHSButtonV2 from '@/components/affiliates/NFHSButtonV2';
import Link from 'components/Link';
import TeamImage from 'components/TeamImage';
import GameResultText from '@/components/GameResultText';
import FollowButton from '@/components/FollowButton';
import AttributeRowImage from '@/components/AttributeRowImage';
import { DEFAULT_SCHOOL_COVER_IMAGE } from 'constants.js.erb';
import AttributeRowClaimed from '@/components/AttributeRowClaimed';
import AttributeRowFollowers from '@/components/AttributeRowFollowers';
import AttributeRow from '@/components/AttributeRow';
import TextLink, { TextLinkSize } from '@/components/TextLink';
import NewsFeed from '@/components/modules/NewsFeed';
import { getGenderSportCoverImageSrc } from '@/components/GenderSportCoverImage';
import ClaimEntityButton from '@/components/ClaimEntityButton';
import PageHeader from '@/components/PageHeader';
import MinuteMediaAd from '@/components/ads/MinuteMediaAd';
import StnVideoAd from '@/components/ads/StnVideoAd';
import OutbrainAd from '@/components/ads/OutbrainAd';
import { CURRENT_SCHOOL_YEAR } from 'constants/schoolYears';
import { SectionHeaderSize } from '@/components/SectionHeader';
import { selectTypename } from '@sblive-sports/api-client-js';
import PhotosCalloutModule from '@/components/modules/PhotosCalloutModule';
import filterArticles from 'helpers/filterArticles';
import Clickable from '@/components/Clickable';
import Image from 'components/Image';
import NFHSImageV2 from 'images/icons/NFHSN_Horizontal_Black.png';
import GoFanImage from 'images/icons/go-fan.png';
import dayjs from 'dayjs';

export default entryPoint(function Show({ query: { team } }: { query: TeamsShowQuery }) {
  const { isSmallScreen } = useScreenSize();

  const currentYearPreviousGames = team.previousGames.nodes.filter(game => game.schoolYear === CURRENT_SCHOOL_YEAR);
  const currentYearNextGames = team.nextGames.nodes.filter(game => game.schoolYear === CURRENT_SCHOOL_YEAR);
  let games = [];

  if (currentYearNextGames.length === 0) {
    games = currentYearPreviousGames.slice(0, 7).reverse();
  } else if (currentYearPreviousGames.length === 0) {
    games = currentYearNextGames.slice(0, 7);
  } else if (currentYearNextGames.length < 7) {
    const neededPrevious = 7 - currentYearNextGames.length;
    const previousGamesToShow = currentYearPreviousGames.slice(0, neededPrevious).reverse();
    games = [...previousGamesToShow, ...currentYearNextGames];
  } else {
    games = [...currentYearPreviousGames.slice(0, 2).reverse(), ...currentYearNextGames].slice(0, 7);
  }

  const nextGame = currentYearNextGames[0];
  const lastGame = currentYearPreviousGames[0];

  const hasNews = team.previousGames.nodes.length > 0;

  const photoGalleries = team.contentItems.nodes.filter(selectTypename('ContentItemPhotoGallery'));
  const hasPhotos = photoGalleries.length > 0;
  const teamStanding = team.league?.teamStandings.find(teamStanding => teamStanding.team.id === team.id);

  const now = dayjs();

  function truncateTeamStandings<T extends { team: { id: string } }>(teamStandings: T[]) {
    const teamIndex = teamStandings.findIndex(({ team: { id } }) => id === team.id)!;

    const gap = teamIndex === teamStandings.length - 1 ? 2 : 1;
    const startIndex = Math.max(0, teamIndex - gap);
    const endIndex = Math.min(startIndex + 3, teamStandings.length);

    return teamStandings.slice(startIndex, endIndex);
  }

  const teamInfo = (
    <ContentSection>
      <ContentSectionHeader right={<FollowButton entityId={team.id} />} size={SectionHeaderSize.LARGE}>
        Team Profile
      </ContentSectionHeader>
      <AttributeRowImage src={team.coverImage || DEFAULT_SCHOOL_COVER_IMAGE} alt={team.name} />
      <AttributeRowClaimed isClaimed={team.isOwned} entityType={ClaimableEntityType.Team} entityId={team.id} />
      {team.fansCount > 0 && <AttributeRowFollowers count={team.fansCount} />}
      {team.league && (
        <AttributeRow title="League">
          <TextLink size={TextLinkSize.SMALL} href={team.league.webPath}>
            {team.league.fullName || team.league.name}
          </TextLink>
        </AttributeRow>
      )}
      {team.headCoach && <AttributeRow title="Head Coach">{team.headCoach.coach.name}</AttributeRow>}
      {team.rival && (
        <AttributeRow title="Rival Team">
          <TextLink href={team.rival.webPath} size={TextLinkSize.SMALL}>
            <TeamImage alt={team.rival.name} src={team.rival.image} className={styles.RivalLogo} />
            {team.rival.name}
          </TextLink>
        </AttributeRow>
      )}
      {teamStanding && (
        <AttributeRow title="Season Record">
          {teamStanding.standing.overallRecord} ({teamStanding.standing.leagueRecord})
        </AttributeRow>
      )}
    </ContentSection>
  );

  const gameFooter = (contest: TeamsShowQuery['team']['nextGames']['nodes'][number]['contest'], isSameDay: boolean, isFuture: boolean) => {
    if (isFuture && !isSameDay && contest?.gofanTicketingUrl) {
      return (
        <Clickable href={contest.nfhsNetworkUrl} className={`${styles.HorizontalScoreboardFooter} ${styles.affiliate}`}>
          <Image src={GoFanImage} style={{ height: '21px' }} />
        </Clickable>
      );
    }
    if ((isSameDay && contest?.nfhsNetworkUrl) || (!contest?.gofanTicketingUrl && contest?.nfhsNetworkUrl)) {
      return (
        <Clickable href={contest.nfhsNetworkUrl} className={`${styles.HorizontalScoreboardFooter}`}>
          <Image src={NFHSImageV2} style={{ width: '117px' }} />
        </Clickable>
      );
    }
    return <div className={styles.HorizontalScoreboardFooter}>Details</div>;
  };

  return (
    <Page subNav={<TeamSubNav team={team} />}>
      <PageLayout
        header={
          games.length > 0 &&
          !isSmallScreen && (
            <ContentSection>
              <ContentSectionHeader
                right={
                  <ContentSectionHeaderLink href={team.webSchedulePath} forward>
                    Full Schedule
                  </ContentSectionHeaderLink>
                }
                size={SectionHeaderSize.LARGE}
              >
                Schedule
              </ContentSectionHeader>
              <div className={styles.HorizontalScoreboard}>
                {games.map(game => {
                  const isGameDay = dayjs(game.date).isSame(now, 'day');
                  const isFuture = dayjs(game.date) > now;
                  return (
                    <Link href={game.webPath} className={styles.HorizontalScoreboardGame} key={game.id}>
                      <div className={styles.HorizontalScoreboardContent}>
                        <LocalizedDate date={game.date} format="ddd, MMMM D" />
                        <div className={styles.HorizontalScoreboardOpponent}>
                          <div>{game.featured.locationDescriptor}</div>
                          <TeamImage
                            src={game.opponent.team.image}
                            alt={game.opponent.team.name}
                            className={styles.HorizontalScoreboardImage}
                          />
                        </div>
                        <div>
                          <GameResultText game={game}>{game.shortStatusText}</GameResultText>
                        </div>
                      </div>
                      {gameFooter(game.contest, isGameDay, isFuture)}
                    </Link>
                  );
                })}
              </div>
            </ContentSection>
          )
        }
        sidebar={
          <>
            {nextGame && (
              <CalloutModule
                title="Next Game"
                right={<ApplicationShareButton path={nextGame.webPath} title={nextGame.titleText} />}
                topText={<LocalizedDate date={nextGame.date} format="ddd, MMMM D" />}
                entity={nextGame.opponent.team}
                bottomText={
                  <>
                    <span>{nextGame.featured.locationDescriptor}</span> {nextGame.opponent.team.name} {nextGame.opponent.team.mascot}
                  </>
                }
                footer={
                  <>
                    {nextGame.statusId !== COMPLETED && nextGame.contest?.nfhsNetworkUrl && (
                      <NFHSButtonV2 href={nextGame.contest.nfhsNetworkUrl}>Subscribe To Watch On</NFHSButtonV2>
                    )}
                    {nextGame.statusId !== COMPLETED && nextGame.contest?.gofanTicketingUrl && (
                      <GoFanButton href={nextGame.contest.gofanTicketingUrl}>Buy Tickets</GoFanButton>
                    )}
                    <Button variant={ButtonVariant.WHITE} width={ButtonWidth.FULL} size={ButtonSize.LARGE} href={nextGame.webPath}>
                      Game Preview
                    </Button>
                  </>
                }
              />
            )}
            {lastGame && (
              <CalloutModule
                title="Last Game"
                right={<ApplicationShareButton path={lastGame.webPath} title={lastGame.headline} />}
                topText={<LocalizedDate date={lastGame.date} format="ddd, MMMM D" />}
                entity={lastGame.opponent.team}
                bottomText={
                  <>
                    <span>{lastGame.featured.locationDescriptor}</span> {lastGame.opponent.team.name} {lastGame.opponent.team.mascot}
                  </>
                }
                footer={
                  <>
                    <Button variant={ButtonVariant.WHITE} width={ButtonWidth.FULL} size={ButtonSize.LARGE} href={lastGame.webPath}>
                      Game Recap
                    </Button>
                    {lastGame.statusId !== UPCOMING && lastGame.contest?.nfhsNetworkUrl && (
                      <NFHSButtonV2 href={lastGame.contest.nfhsNetworkUrl}>Watch Replay On</NFHSButtonV2>
                    )}
                  </>
                }
              />
            )}
            <MinuteMediaAd placement={isSmallScreen && !hasNews ? 'IN_FEED' : 'SIDEBAR'} />
            {hasPhotos && (
              <PhotosCalloutModule
                title="Pro Photo Galleries"
                right={<ApplicationShareButton path={team.webPhotosPath} title={`${team.name} Photos`} />}
                topText={`Check out photos of the ${team.mascot} in action from the SBLive Professional Photography Network!`}
                photoGalleries={photoGalleries}
                footer={
                  <Button variant={ButtonVariant.GRAY} width={ButtonWidth.FULL} size={ButtonSize.LARGE} href={team.webPhotosPath}>
                    See All {team.name} {team.genderSport.name} Galleries
                  </Button>
                }
              />
            )}
            {hasNews && teamInfo}
            {team.league && (
              <ContentSection>
                <ContentSectionHeader
                  right={<ContentSectionHeaderLink href={team.webStandingsPath}>Full Standings</ContentSectionHeaderLink>}
                  size={SectionHeaderSize.LARGE}
                >
                  {team.league.label} Standings
                </ContentSectionHeader>
                <ContentSectionFlushContent>
                  <StandingsTable
                    isHighlighted={({ team: { id } }) => id === team.id}
                    teamStandings={truncateTeamStandings(team.league.teamStandings)}
                  />
                </ContentSectionFlushContent>
                <ContentSectionFooterButton href={team.webStandingsPath}>Full Standings</ContentSectionFooterButton>
              </ContentSection>
            )}
            <MinuteMediaAd placement={isSmallScreen && !hasNews ? 'IN_FEED_2' : 'SIDEBAR_2'} />
            {!team.isOwned && (
              <ContentSection>
                <ContentSectionHeader size={SectionHeaderSize.LARGE}>Keep This Page Updated</ContentSectionHeader>
                <InfoBlock
                  title="Are You The Coach?"
                  footer={
                    <ClaimEntityButton entityId={team.id} width={ButtonWidth.FULL}>
                      Claim This Team
                    </ClaimEntityButton>
                  }
                >
                  If you are a coach for this team, you can claim your team to manage the schedule, roster, stats and more. Crunched for
                  time? Don’t worry, team owners can invite volunteer admins to manage this page for them.
                </InfoBlock>
                {/*<InfoContentBlock*/}
                {/*  title="Invite The Coach"*/}
                {/*  body={*/}
                {/*    'This team hasn’t been claimed yet! Invite the head coach to claim this team to start updating this page. We can help them login and get started with managing rosters, schedules, scores and more.'*/}
                {/*  }*/}
                {/*  buttonText="Submit Game Details Correction"*/}
                {/*/>*/}
              </ContentSection>
            )}
          </>
        }
      >
        {hasNews ? (
          <ContentSection>
            <PageHeader>News & Updates</PageHeader>
            <NewsFeed
              games={team.previousGames.nodes.filter(
                (game): game is (typeof team)['previousGames']['nodes'][number] & { headline: string } => game.headline !== undefined
              )}
              photoGalleries={team.contentItems.nodes.filter(selectTypename('ContentItemPhotoGallery'))}
              defaultAuthorImage={team.image}
              defaultImage={getGenderSportCoverImageSrc(team.genderSport.key)}
              articles={filterArticles(team.articles.nodes)}
            />
          </ContentSection>
        ) : (
          <>
            {teamInfo}
            <StnVideoAd placement="INLINE" />
          </>
        )}
        <OutbrainAd />
      </PageLayout>
    </Page>
  );
});
